/**
 * LocationPermissionBanner - Prompts user to enable location for accurate distances
 * 
 * Shows on PS Golfers page when location is not enabled.
 * Can be dismissed, but will show again on next visit.
 */

import React, { useState, useEffect } from 'react';
import { MapPin, X, Navigation } from 'lucide-react';
import { useLocationContext } from '../hooks/LocationProvider';

export function LocationPermissionBanner({ onClose }) {
  const { 
    permissionStatus, 
    enableLocationTracking, 
    hasLocation,
    isTracking,
    error 
  } = useLocationContext();
  
  const [isLoading, setIsLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Don't show if already tracking or dismissed
  if (hasLocation || isTracking || dismissed) return null;
  
  // Don't show if permission was denied (they'll need to change settings)
  if (permissionStatus === 'denied') return null;

  const handleEnable = async () => {
    setIsLoading(true);
    const success = await enableLocationTracking();
    setIsLoading(false);
    if (success && onClose) {
      onClose();
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onClose) onClose();
  };

  return (
    <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4 mb-4 relative animate-fade-in">
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-white transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-emerald-500/30 rounded-full flex items-center justify-center shrink-0">
          <Navigation className="w-5 h-5 text-emerald-400" />
        </div>
        
        <div className="flex-1">
          <h4 className="text-white font-semibold text-sm mb-1">
            Enable Location for Accurate Distances
          </h4>
          <p className="text-gray-400 text-xs mb-3">
            See how far away other golfers are. Your location is only used while the app is open.
          </p>
          
          <button
            onClick={handleEnable}
            disabled={isLoading}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Enabling...
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4" />
                Enable Location
              </>
            )}
          </button>
          
          {error && (
            <p className="text-red-400 text-xs mt-2">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * LocationDeniedBanner - Shows when user denied location permission
 * Provides instructions on how to re-enable
 */
export function LocationDeniedBanner() {
  const { permissionStatus } = useLocationContext();
  const [dismissed, setDismissed] = useState(false);
  
  if (permissionStatus !== 'denied' || dismissed) return null;

  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4 relative">
      <button 
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-white transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center shrink-0">
          <MapPin className="w-5 h-5 text-amber-400" />
        </div>
        
        <div className="flex-1">
          <h4 className="text-amber-400 font-semibold text-sm mb-1">
            Location Access Blocked
          </h4>
          <p className="text-gray-400 text-xs">
            To see accurate distances to other golfers, enable location in your browser settings, 
            then refresh the page.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact location status indicator for headers/cards
 */
export function LocationStatusIndicator() {
  const { hasLocation, isTracking, accuracy } = useLocationContext();
  
  if (!isTracking && !hasLocation) return null;
  
  return (
    <div className="flex items-center gap-1.5 text-xs text-emerald-400">
      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
      <span>
        {accuracy ? `±${Math.round(accuracy)}m` : 'GPS'}
      </span>
    </div>
  );
}

export default LocationPermissionBanner;
